export const ACCOUNTING_METHOD_SELECTION_MODAL_PATH =
  'accounting-method-selection';

export const ACCOUNTING_METHOD_PROMPT_MODES = {
  INITIAL_SELECTION: 'INITIAL_SELECTION',
  UPGRADE_CONFIRMATION: 'UPGRADE_CONFIRMATION',
} as const;

export const ACCOUNTING_METHODS = {
  NONE: 'NONE',
  CASH: 'CASH',
  ACCRUAL: 'ACCRUAL',
} as const;

export type AccountingMethodValue =
  (typeof ACCOUNTING_METHODS)[keyof typeof ACCOUNTING_METHODS];

export const isAccountingMethodValue = (
  accountingMethod: string
): accountingMethod is AccountingMethodValue =>
  Object.values(ACCOUNTING_METHODS).includes(
    accountingMethod as AccountingMethodValue
  );

export const ACCOUNTING_METHOD_VALIDATION_MESSAGES = {
  ACCOUNTING_METHOD: 'Please choose an accounting method before continuing.',
  ACCEPTED_AGREEMENT:
    'You must agree to the accounting method terms before continuing.',
  SIGNER_NAME_AND_DATE: 'Please enter your full name and signing date.',
  SIGNER_NAME: 'Please enter your full name.',
  SIGNING_DATE: 'Please enter the signing date.',
} as const;

export const ACCOUNTING_METHOD_DISPLAY_ORDER: AccountingMethodValue[] = [
  ACCOUNTING_METHODS.NONE,
  ACCOUNTING_METHODS.CASH,
  ACCOUNTING_METHODS.ACCRUAL,
];

export const ACCOUNTING_METHOD_LABELS: Record<AccountingMethodValue, string> = {
  [ACCOUNTING_METHODS.ACCRUAL]: 'Accrual',
  [ACCOUNTING_METHODS.CASH]: 'Cash',
  [ACCOUNTING_METHODS.NONE]: 'None',
};

export const ACCOUNTING_METHOD_COPY: Record<
  AccountingMethodValue,
  {
    title: string;
    description: string;
    bullets: string[];
  }
> = {
  [ACCOUNTING_METHODS.NONE]: {
    title: 'None',
    description: '',
    bullets: [],
  },
  [ACCOUNTING_METHODS.CASH]: {
    title: 'Cash Basis',
    description: 'Recognize income and expenses when money changes hands.',
    bullets: [
      'Usually simpler for smaller or straightforward portfolios',
      'Matches cash in and cash out timing',
      'Often easier to understand during setup',
    ],
  },
  [ACCOUNTING_METHODS.ACCRUAL]: {
    title: 'Accrual basis',
    description:
      'Recognize income and expenses when they are earned or incurred.',
    bullets: [
      'Shows obligations and income as they happen',
      'Useful when reporting needs go beyond simple cash tracking',
      'May align better with accountant-led processes',
    ],
  },
};

export const ACCOUNTING_METHOD_COPY_PLACEHOLDERS = {
  ENTITY_LABEL: '%entityLabel%',
  AUTHORITY_LABEL: '%authorityLabel%',
} as const;

export const ACCOUNTING_METHOD_IMPORTANT_NOTICE_TEMPLATES = [
  `Once this ${ACCOUNTING_METHOD_COPY_PLACEHOLDERS.ENTITY_LABEL} has financial transactions, the accounting method can no longer be changed.`,
  `This choice is intended to be permanent for the ${ACCOUNTING_METHOD_COPY_PLACEHOLDERS.ENTITY_LABEL}, so it should be confirmed carefully before proceeding.`,
  'If you are unsure which method is right for your business, consult your accountant before continuing.',
] as const;

export const ACCOUNTING_METHOD_BEFORE_CONTINUE_TEMPLATES = [
  'You understand the difference between Cash and Accrual accounting.',
  'You acknowledge this selection cannot be changed after transactions exist.',
  'You agree to take responsibility for this choice.',
  `You have authority to make this selection for ${ACCOUNTING_METHOD_COPY_PLACEHOLDERS.AUTHORITY_LABEL}.`,
] as const;
